<!--
  Component to render <search-box> element
  - Accepts the following properties:
  [id, type, name, placeholder, value, label, list]

  - Accepts optional callback property:
  [on-search-box-change, on-search-box-item-selected]

  @example

    <search-box
      ref="searchinput1"
      :id="my-123"
      :label="Search locations"
      :list='[{"id":9,"name":"Hello world","city":"San Luis"},{"id":10,"name":"Another test","city":"Santa Clara CA"}]'
      @on-search-box-item-selected="onSearchBoxItemSelected"
    />

  -->

<template lang="html">
  <section class="m-search-box">
    <div v-if="hasDragIcon">
      <svg class="list-drag-item-icon">
        <use xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#rows" />
      </svg>
    </div>
    <div
      class="m-search-box"
      :id="id"
      v-clickoutside="onClickedOutside"
      @keydown.esc.stop="onEsc"
    >
      <div class="m-search-box">
        <label v-if="label" class="m-text-input__label">{{ label }}</label>
        <div class="m-selection" v-if="selectedItem && !selectedItem.isEmpty">
          <div class="m-item-selected">
            <div class="m-selection-label">
              <svg
                class="selected-icon slds-icon_container slds-icon--x-small slds-icon-text-default slds-m-top_xx-small"
                v-if="hasIcon"
              >
                <use :xlink:href="getUtilityIcon(selectedItem)"></use>
              </svg>
              <div class="m-content-selection">
                <span
                  v-if="
                    selectedItem.type === expressionType.STATIC &&
                      selectedItem.dataType === expressionType.STRING
                  "
                >
                  &quot;{{ selectedItem.value }}&quot;
                </span>
                <span v-else-if="selectedItem.type === expressionType.STATIC"
                  ><span>{{ selectedItem.value }}</span></span
                >
                <span v-else>{{
                  selectedItem.label || selectedItem.name
                }}</span>
              </div>
            </div>
            <div
              class="m-content-selection m-label-content-type"
              v-if="
                selectedItem.isTemplate !== undefined &&
                  selectedItem.isTemplate !== null
              "
            >
              {{ pageTypeLabel(selectedItem) }}
            </div>
            <svg
              class="m-close-selection-icon"
              tabindex="0"
              @keydown.enter.stop="closeSelected"
              v-on:click="closeSelected"
            >
              <use
                xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#close"
              ></use>
            </svg>
          </div>
        </div>
        <div
          v-if="!selectedItem || selectedItem.isEmpty"
          class="m-search-container"
        >
          <text-input
            :class="{ 'm-invalid': isInvalidInput }"
            ref="textInputItem"
            :focus="shouldFocus"
            v-model="currentTextValue"
            :type="type"
            :placeholder="placeholderText"
            @on-text-input-focus="onInputFocus"
            @on-text-input-blur="onInputBlur"
            @on-text-input-change="onInputChanged"
            @click="onInputFocus"
            @on-up-arrow-pressed="onUpArrowPressed"
            @on-down-arrow-pressed="onDownArrowPressed"
            @on-tab-pressed="closeDropdown"
            @on-single-select-press-enter="onSingleSelectPressEnter"
          />
          <svg class="m-input-search-icon">
            <use
              xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#search"
            ></use>
          </svg>
        </div>
      </div>
      <div ref="resultList" class="m-result-list" v-if="showResultsList">
        <ul>
          <li
            class="m-item"
            v-if="hasStaticDateExp"
            :class="getClassForItem(0)"
          >
            <hardcoded-date
              :exp="exp"
              @on-search-box-item-selected="onSearchBoxItemSelected"
            />
          </li>
          <li
            class="m-item"
            v-if="hasStaticNumberExp"
            :class="getClassForItem(0)"
          >
            <hardcoded-number
              :exp="exp"
              @on-search-box-item-selected="onSearchBoxItemSelected"
            />
          </li>
          <li
            class="m-item"
            v-if="hasStaticStringExp"
            :class="getClassForItem(0)"
          >
            <hardcoded-string
              :has-new-token="hasNewToken"
              :is-from-concat="isFromConcat"
              :exp="exp"
              @on-search-box-item-selected="onSearchBoxItemSelected"
            />
          </li>

          <hardcoded-boolean
            :exp="exp"
            v-if="hasStaticBooleanExp"
            @on-search-box-item-selected="onSearchBoxItemSelected"
          />

          <li
            class="m-item"
            :class="getClassForItem(index, item)"
            v-for="(item, index) in updateList"
            :key="index"
          >
            <search-box-list-item-simple
              class="m-list-option-text"
              :item-id="`${id}-${index}`"
              :list-item="item"
              :exp="exp"
              :has-icon="hasIcon"
              @on-search-box-item-selected="onSearchBoxItemSelected"
            />
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script lang="js">
  import debounce from "lodash.debounce";
  import { EXPRESSIONS_TYPE } from "@/static/config-list-components";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import TextInput from "@/components/TextInput";
  import SearchBoxListItemSimple from "@/components/SearchBoxListItemSimple";
  import HardcodedBoolean from "@/components/hardcoded-expressions/HardcodedBoolean";
  import HardcodedDate from "@/components/hardcoded-expressions/HardcodedDate";
  import HardcodedNumber from "@/components/hardcoded-expressions/HardcodedNumber";
  import HardcodedString from "@/components/hardcoded-expressions/HardcodedString";
  import store from "@/store";
  import iconsUri from "@/views/drag-drop-pages/components/utils/icons-uri";

  export default  {
    name: 'search-box-simple',
    components: {
      TextInput,
      SearchBoxListItemSimple,
      HardcodedBoolean,
      HardcodedDate,
      HardcodedNumber,
      HardcodedString
    },
    props: {
      /**
       * input properties
       * @values id, type, name, placeholder, value, label, list
       */
      id: {
        type: String
      },
      type: {
        type: String,
        default: "text"
      },
      name: {
        type: String
      },
      placeholder: {
        type: String
      },
      value: {
        type: Object
      },
      textValue: {
        type: String
      },
      label: {
        type: String
      },
      list: {
        type: Array
      },
      isInvalid: {
        type: Boolean,
        value: false
      },
      hasNewToken: {
        type: Boolean,
        value: false
      },
      isFromConcat: {
        type: Boolean,
        value: false
      },
      staticDataType: {},
      isGlobal: {
        type: Boolean
      },
      isFirstInput: {
        type: Boolean,
        default: false
      },
      shouldFocus: {
        type: Boolean
      },
      hasIcon: {
        type: Boolean,
        value: false
      },
      hasDragIcon: {
        type: Boolean,
        value: false
      },
    },
    data () {
      return {
        updateList: [],
        selectedItem: null,
        exp: "",
        showResultsList: false,
        isInvalidInput: false,
        currentTextValue: null,
        staticExpressionsType: [],
        expressionType: {
          STRING: expressionBuilder.EXPRESSION_TYPE.STRING,
          NUMBER: expressionBuilder.EXPRESSION_TYPE.NUMBER,
          DATE: expressionBuilder.EXPRESSION_TYPE.DATE,
          BOOLEAN: expressionBuilder.EXPRESSION_TYPE.BOOLEAN,
          STATIC: expressionBuilder.EXPRESSION_TYPE.STATIC,
          LOCAL_FIELD: expressionBuilder.EXPRESSION_TYPE.LOCAL_FIELD
        },
        selectedId: -1,
        isInputInFocus: false
      }
    },
    watch: {
      textValue: function() {
        this.currentTextValue = "";
        this.$nextTick(() => {
          this.currentTextValue = this.textValue;
        });
      },
      value: {
        handler() {
          this.selectedItem = this.value;
        },
        immediate: true
      },
      list: {
        handler() {
          this.setList();
        },
        immediate: true,
        deep: true
      },
      isInvalid: function() {
        this.setInvalidValue();
      },
      isGlobal: function() {
        this.setList();
      },
      staticDataType: function() {
        this.setupStaticExpressionType();
      }
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      hasStaticStringExp() {
        return this.staticExpressionsType.includes(this.expressionType.STRING);
      },
      hasStaticNumberExp() {
        return this.staticExpressionsType.includes(this.expressionType.NUMBER);
      },
      hasStaticDateExp() {
        return this.staticExpressionsType.includes(this.expressionType.DATE);
      },
      hasStaticBooleanExp() {
        return this.staticExpressionsType.includes(this.expressionType.BOOLEAN);
      },
      placeholderText() {
        if (typeof this.placeholder === 'string' || this.placeholder instanceof String) {
          return this.placeholder;
        } else {
          return this.getMessageText(this.messages.SEARCH);
        }
      }
    },
    mounted() {
      this.setupStaticExpressionType();
      this.setList();
    },
    methods: {
      pageTypeLabel(selectedItem) {
        return selectedItem.isTemplate ? this.$t("pageTemplate") : this.$t("page");
      },
      getClassForItem(index, item) {
        if (this.selectedId < 0 || !this.$refs.resultList) {
          return "";
        }

        const rawList = this.$refs.resultList.children[0].children;
        const filteredList =
          Array.prototype.filter.call(rawList,
            (c) => c.children.length > 0);
        const hasStaticExpOnTop = rawList[index] === filteredList[index];

        let result;
        if (!item) {
          result = (index === this.selectedId && hasStaticExpOnTop) ? 'item-on-hover-highlight' : "";
        } else {
          let newIndex = index;
          if (hasStaticExpOnTop
            && (this.hasStaticNumberExp
              || this.hasStaticBooleanExp
              || this.hasStaticStringExp
              || this.hasStaticDateExp)
          ) {
            newIndex = index + 1;
          }
          result = newIndex === this.selectedId ? 'item-on-hover-highlight' : "";
        }

        return result;
      },
      selectAnItem(id) {
        if (this.$refs.resultList) {
          this.selectedId = id;
          const filteredList =
            Array.prototype.filter.call(this.$refs.resultList.children[0].children,
              (c) => c.children.length > 0);
          if (filteredList[id]) {
            filteredList[id].scrollIntoView(false);
          }
        }
      },
      onUpArrowPressed() {
        if (this.selectedId > 0) {
          this.selectAnItem(this.selectedId - 1);
        }
      },
      onDownArrowPressed() {
        const filteredList =
          Array.prototype.filter.call(this.$refs.resultList.children[0].children,
            (c) => c.children.length > 0);
        if (this.selectedId < filteredList.length - 1) {
          this.selectAnItem(this.selectedId + 1);
        }
      },
      onEsc() {
        this.closeDropdown();
      },
      onClickedOutside() {
        if (!this.isInputInFocus) {
          this.closeDropdown();
        }
      },
      closeDropdown() {
        this.showResultsList = false;
        this.isInputInFocus = false;
      },
      setupStaticExpressionType() {
        this.staticExpressionsType = [];
        if(this.staticDataType) {
          if(Array.isArray(this.staticDataType)) {
            for(const dt of this.staticDataType) {
              this.staticExpressionsType.push(dt.toLowerCase());
            }
          } else {
            this.staticExpressionsType.push(this.staticDataType.toLowerCase());
          }
        }
      },
      setList() {
        this.updateList = [];
        this.exp = "";
        const localTypes = Object.values(EXPRESSIONS_TYPE.LOCAL_ONLY).map(el => el.type.toLowerCase());
        if(this.list) {
          for(const el of this.list) {
            const value = {...el};
            delete value.expression;  // make sure no expression value is attached
            if(this.isGlobal) {
              // do not add if local only
              if(value.type.toLowerCase() !== this.expressionType.LOCAL_FIELD && !localTypes.includes(value.type.toLowerCase())) {
                this.updateList.push(value);
              }
            } else {
              this.updateList.push(value);
            }
          }
        }
      },
      setInvalidValue() {
        this.isInvalidInput = this.isInvalid;
      },
      closeSelected() {
        this.selectedItem = this.id ? {searchBoxId: this.id, isEmpty: true} : null;
        this.exp = "";
        this.$emit("on-search-box-item-selected", this.selectedItem);
      },
      onSearchBoxItemSelected(item) {
        if (this.id) {
          item.searchBoxId = this.id;
        }
        this.selectedItem = item;
        /**
         * Triggers when selectedItem value changes
         * @event on-search-box-item-selected
         */
        if(this.$listeners && this.$listeners["on-search-box-item-selected"]) {
          this.$emit("on-search-box-item-selected", this.selectedItem);
        }
        this.closeDropdown();
      },
      getUtilityIcon(item) {
        return iconsUri.getUtilityIconByType(item.dataType);
      },
      onInputFocus() {
        this.isInputInFocus = true;
        this.showResultsList = true;
        this.isInvalidInput = false;
      },
      onInputBlur() {
        this.isInputInFocus = false;
        setTimeout(() => {
          this.setInvalidValue(); // we don't know if it is valid or not, so let the user to validate again

          if(this.$listeners && this.$listeners["on-search-box-blur"]) {
            this.$emit("on-search-box-blur", this.exp, this.id || null);
          }
        }, 200);
      },
      onInputChanged: debounce(function(val) {
        /**
         * Triggers when model value changes
         * @event on-search-box-change
         */
        this.selectedId = -1;
        if(this.$listeners && this.$listeners["on-search-box-change"]) {
          this.$emit("on-search-box-change", val, this.id || null);
        }
        this.exp = val;
      }, 400),
      onSingleSelectPressEnter() {
        if (this.selectedId >= 0) {
          const filteredList =
            Array.prototype.filter.call(this.$refs.resultList.children[0].children,
              (c) => c.children.length > 0);
          if (filteredList[this.selectedId] && filteredList[this.selectedId].children[0]) {
            filteredList[this.selectedId].children[0].click();
          }
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";

  .fields-icon {
    float: left;
    margin-top: 12px;
    margin-left: 8px;
  }
  .selected-icon {
    margin-left: 4px;
  }
  .m-selection-label {
    display: flex;
    overflow: hidden;
  }
  .item-on-hover-highlight {
    background-color: var(--HEADER_GRAY);
  }
  .list-drag-item-icon {
    height: 1.8rem;
    width: 1rem;
    fill: #939495;
    cursor: move;
    margin-right: -1rem;
    margin-left: 0.15rem;
    margin-bottom: -3rem;
    transform: translateX(-1.1rem);
    padding-left: 0.1rem;
    padding-right: 0.1rem;
  }
  .m-label-content-type {
    margin-left: auto;
    color: var(--TEXT_GREY_LIGHT);
  }
</style>
